import React, { useCallback, useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import ChannelsService from '../../services/ChannelsService';
import { darkStyles } from './MultiSelect';
import { theme } from '../../theme';

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 350;

function CategoryMultiSelect({ type, value = [], onChange, placeholder = 'Buscar categorías...' }) {
    const [optionMap, setOptionMap] = useState({});
    const [options, setOptions] = useState([]);
    const [search, setSearch] = useState('');
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const pageRef = useRef(1);
    const searchRef = useRef('');
    const loadedAllRef = useRef(false);
    const reqSeq = useRef(0);

    const hydrate = useCallback(async (codes) => {
        const missing = codes.filter((c) => c && !optionMap[c]);
        if (missing.length === 0) return;
        try {
            const res = await ChannelsService.getCategories({ type, codes: missing.join(',') });
            const map = {};
            (res.categories || []).forEach((cat) => {
                map[cat.code] = { value: cat.code, label: `${cat.code} - ${cat.title || ''}` };
            });
            setOptionMap((prev) => ({ ...prev, ...map }));
        } catch (e) {
            // Las etiquetas caen al codigo como fallback; no bloquea la edicion.
        }
    }, [type, optionMap]);

    useEffect(() => {
        hydrate([...new Set(value)]);
    }, [value, hydrate]);

    const load = useCallback(async (q, page) => {
        const seq = ++reqSeq.current;
        setLoading(true);
        try {
            const res = await ChannelsService.getCategories({ type, q, page, per_page: PAGE_SIZE });
            if (seq !== reqSeq.current) return;
            const cats = res.categories || [];
            const map = {};
            cats.forEach((cat) => {
                map[cat.code] = { value: cat.code, label: `${cat.code} - ${cat.title || ''}` };
            });
            setOptionMap((prev) => ({ ...prev, ...map }));
            const list = cats.map((cat) => ({ value: cat.code, label: `${cat.code} - ${cat.title || ''}` }));
            setOptions((prev) => (page === 1 ? list : mergeUnique(prev, list)));
            setTotal(res.total || 0);
            loadedAllRef.current = (page * PAGE_SIZE) >= (res.total || 0);
        } catch (e) {
            setOptions([]);
        } finally {
            if (seq === reqSeq.current) setLoading(false);
        }
    }, [type]);

    const debouncedLoad = useRef(null);

    const handleInputChange = (text) => {
        setSearch(text);
        searchRef.current = text;
        clearTimeout(debouncedLoad.current);
        debouncedLoad.current = setTimeout(() => {
            pageRef.current = 1;
            load(text, 1);
        }, DEBOUNCE_MS);
    };

    const handleOpen = () => {
        if (options.length === 0 && !loading) {
            pageRef.current = 1;
            load(searchRef.current, 1);
        }
    };

    const handleMenuScrollToBottom = () => {
        if (loading || loadedAllRef.current) return;
        pageRef.current += 1;
        load(searchRef.current, pageRef.current);
    };

    const searchCodes = (options || []).map((o) => o.value);
    const extraCodes = value.filter((c) => c && !searchCodes.includes(c) && !options.some((o) => o.value === c));
    const menuOptions = [
        ...options,
        ...extraCodes.map((c) => optionMap[c] || { value: c, label: `${c} - …` }),
    ];

    return (
        <Select
            isMulti
            options={menuOptions}
            value={value.map((c) => optionMap[c] || { value: c, label: `${c} - …` })}
            onChange={(sel) => onChange((sel || []).map((o) => o.value))}
            placeholder={placeholder}
            styles={darkStyles}
            isLoading={loading}
            onInputChange={handleInputChange}
            inputValue={search}
            onMenuOpen={handleOpen}
            onMenuScrollToBottom={handleMenuScrollToBottom}
            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
            theme={(themeObj) => ({
                ...themeObj,
                colors: { ...themeObj.colors, primary: theme.primary },
            })}
        />
    );
}

function mergeUnique(prev, list) {
    const seen = new Set(prev.map((o) => o.value));
    return [...prev, ...list.filter((o) => !seen.has(o.value))];
}

export default CategoryMultiSelect;